import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import InputLabel from "@mui/material/InputLabel";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

export const userUi = {
	pageBg: grey[50],
	surface: "#ffffff",
	border: grey[200],
	borderStrong: grey[300],
	textPrimary: grey[900],
	textSecondary: grey[600],
	textMuted: grey[500],
	radius: 2,
	shadow: "0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)",
	type: {
		INTERNAL: { bg: "#EFF8FF", color: "#175CD3", border: "#B2DDFF" },
		EXTERNAL: { bg: "#F4F3FF", color: "#5925DC", border: "#D9D6FE" },
	},
	admin: { bg: "#FEF6EE", color: "#B54708", border: "#F9DBAF" },
};

export const UserTypeChip = ({ label, size = "small" }) => {
	const tone = userUi.type[label] || userUi.type.INTERNAL;
	return (
		<Chip
			label={label}
			size={size}
			sx={{
				height: 24,
				fontWeight: 600,
				fontSize: 12,
				letterSpacing: 0.2,
				bgcolor: tone.bg,
				color: tone.color,
				border: `1px solid ${tone.border}`,
				borderRadius: 1.5,
				"& .MuiChip-label": { px: 1 },
			}}
		/>
	);
};

export const UserRoleChip = ({ label, onDelete, isAdmin = false }) => {
	const tone = isAdmin ? userUi.admin : { bg: grey[50], color: grey[800], border: grey[200] };
	return (
		<Chip
			label={label}
			size="small"
			onDelete={onDelete}
			sx={{
				height: 26,
				fontWeight: 500,
				fontSize: 12,
				flexShrink: 0,
				bgcolor: tone.bg,
				color: tone.color,
				border: `1px solid ${tone.border}`,
				borderRadius: 1.5,
				"& .MuiChip-label": { px: 1 },
			}}
		/>
	);
};

export const UserPageHeader = ({ title, subtitle, actions, sx = {} }) => (
	<Stack
		direction={{ xs: "column", sm: "row" }}
		alignItems={{ xs: "flex-start", sm: "center" }}
		justifyContent="space-between"
		spacing={1.5}
		sx={{ mb: 2, ...sx }}>
		<Box minWidth={0}>
			<Typography
				variant="h5"
				sx={{
					fontWeight: 600,
					fontSize: { xs: 24, md: 28 },
					letterSpacing: "-0.02em",
					color: userUi.textPrimary,
					lineHeight: 1.25,
				}}>
				{title}
			</Typography>
			{subtitle ? (
				<Typography variant="body2" sx={{ color: userUi.textSecondary, mt: 0.5, fontSize: 15 }}>
					{subtitle}
				</Typography>
			) : null}
		</Box>
		{actions ? (
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
				{actions}
			</Stack>
		) : null}
	</Stack>
);

export const UserStatCard = ({ label, value, selected, onClick, loading = false }) => (
	<Box
		onClick={loading ? undefined : onClick}
		role="button"
		tabIndex={loading ? -1 : 0}
		onKeyDown={(e) => {
			if (loading) return;
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onClick?.();
			}
		}}
		sx={{
			flex: 1,
			minWidth: 140,
			textAlign: "left",
			cursor: loading ? "default" : "pointer",
			p: 1.75,
			border: `1px solid ${selected ? "#B2DDFF" : userUi.border}`,
			borderRadius: userUi.radius,
			bgcolor: selected ? "#EFF8FF" : userUi.surface,
			boxShadow: selected ? "none" : userUi.shadow,
			transition: "border-color 0.15s ease, background-color 0.15s ease",
			"&:hover": { borderColor: selected || loading ? (selected ? "#84CAFF" : userUi.border) : userUi.borderStrong },
		}}>
		<Typography sx={{ fontSize: 13, fontWeight: 500, color: userUi.textSecondary }}>{label}</Typography>
		{loading ? (
			<Skeleton variant="rounded" width={56} height={28} sx={{ mt: 0.75 }} animation="wave" />
		) : (
			<Typography sx={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.03em", color: userUi.textPrimary, mt: 0.25 }}>
				{value}
			</Typography>
		)}
	</Box>
);

export const UserModalHeader = ({ title, subtitle }) => (
	<Box
		sx={{
			px: { xs: 2, md: 3 },
			pt: 2.5,
			pb: 2,
			borderBottom: `1px solid ${grey[200]}`,
			bgcolor: grey[50],
		}}>
		<Typography sx={{ fontWeight: 600, fontSize: 20, letterSpacing: "-0.01em", color: grey[900] }}>{title}</Typography>
		{subtitle ? (
			<Typography variant="body2" sx={{ color: grey[600], mt: 0.5, fontSize: 14 }}>
				{subtitle}
			</Typography>
		) : null}
	</Box>
);

export const modalFormStack = {
	width: { xs: "100%", sm: "47%", md: "31%" },
};

export const modalLabelSx = {
	fontSize: 14,
	fontWeight: 500,
	color: grey[600],
	mb: 0.75,
	letterSpacing: 0.15,
};

export const modalInputSx = {
	bgcolor: "#fff",
	"& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 15 },
	"& .MuiInputBase-input": { fontSize: 15 },
};

export const userModalPaperProps = {
	sx: {
		borderRadius: 2,
		border: `1px solid ${grey[200]}`,
		boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
		overflow: "hidden",
	},
};

export const modalCancelBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	borderColor: grey[300],
	color: grey[900],
	px: 2,
};

export const modalSaveBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	boxShadow: "none",
	px: 2.25,
	"&:hover": { boxShadow: "none" },
};

export const UserFormField = ({ label, required, children, sx = {} }) => (
	<Stack sx={{ ...modalFormStack, ...sx }} spacing={0.75}>
		<InputLabel sx={modalLabelSx}>
			{label}
			{required ? <span style={{ color: "#B42318" }}> *</span> : null}
		</InputLabel>
		{children}
	</Stack>
);

export const UserModalActions = ({
	onCancel,
	submitLabel = "Save",
	cancelLabel = "Cancel",
	disableSubmit = false,
	hideCancel = false,
	extra,
}) => (
	<DialogActions
		sx={{
			px: { xs: 2, md: 3 },
			py: 2,
			borderTop: `1px solid ${grey[200]}`,
			bgcolor: "#fff",
			gap: 1,
		}}>
		{extra}
		{!hideCancel ? (
			<Button variant="outlined" onClick={onCancel} sx={modalCancelBtnSx}>
				{cancelLabel}
			</Button>
		) : null}
		<Button autoFocus variant="contained" type="submit" disabled={disableSubmit} sx={modalSaveBtnSx}>
			{submitLabel}
		</Button>
	</DialogActions>
);

const avatarColors = ["#1D4ED8", "#047857", "#B45309", "#7C3AED", "#BE123C", "#0F766E"];

export const getInitials = (name = "") =>
	name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0])
		.join("")
		.toUpperCase() || "?";

export const colorFromName = (name = "") => avatarColors[(name.charCodeAt(0) || 0) % avatarColors.length];
